import GuiContext from "config/context/guiContext";
import { useContext, useState } from "react";
import { useMfImport } from "../context/MfImportContext";
import { useGetDatabaseStructureService } from './useGetDatabaseStructureService';
import { useUpdatePermissions } from "api/permissions/usePermissions";


const initialServerStatus = {
    response: null,
    loading: false,
    error: null,
}

export const useParseMf = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setTable } = useMfImport();

    const {
        URL
    } = useContext(GuiContext);

    const parseMf = async ({ file, type }) => {

        if (!file) {
            setError('Missing required fields');
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/plugins/${type}_parse`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await res.json();
            setResponse(data);
            setTable(data?.data?.compositions[0]);
            
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };


    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        parseMf,
        resetServerStatus
    };
};

export const useUploadMf = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getDatabaseStructure } = useGetDatabaseStructureService();
    const { updatePermissions } = useUpdatePermissions();

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const uploadMf = async ({ type, data }) => {

        const formData = new FormData();
        const normalizedFile = await normalizeMfFileForNowDefaults(type, data.file, data.compositions);
        formData.append("server_url", selectedServer || "");
        formData.append("database", selectedAccount || "");
        formData.append("permission", data.permission || "");
        formData.append("private_key", data.private_key || "");
        formData.append("exist", data.exist);
        formData.append('scope', data.scope);
        formData.append("compositions", JSON.stringify(data.compositions));
        formData.append("file", normalizedFile);

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/plugins/${type}_import`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await res.json();
            setResponse(data);
            getDatabaseStructure();
            updatePermissions();
            
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };


    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        uploadMf,
        resetServerStatus
    };
};

const normalizeMfFileForNowDefaults = async (type, file, compositions = []) => {
    if (!file) return file;

    const nowDefaultNames = getNowDefaultTimePointNames(compositions?.[0]);
    if (!nowDefaultNames.size) return file;

    if (type === "json") {
        return normalizeJsonMfFile(file, nowDefaultNames);
    }

    if (type === "csv") {
        return normalizeCsvMfFile(file, nowDefaultNames);
    }

    return file;
};

const getNowDefaultTimePointNames = (composition = {}) => {
    const fields = composition?.fields || [];
    const indexes = composition?.indexes || [];
    return new Set(
        [...fields, ...indexes]
            .filter((entry) => entry?.type === "time_point" && String(entry?.default_value || "").trim().toUpperCase() === "NOW()")
            .map((entry) => entry.name),
    );
};

const normalizeJsonMfFile = async (file, nowDefaultNames) => {
    const text = await file.text();
    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch (e) {
        return file;
    }

    const rows = Array.isArray(parsed) ? parsed : [parsed];
    const normalizedRows = rows.map((row = {}) => {
        const normalized = { ...row };
        nowDefaultNames.forEach((name) => {
            if (normalized[name] === undefined || normalized[name] === "") {
                normalized[name] = null;
            }
        });
        return normalized;
    });

    const content = JSON.stringify(Array.isArray(parsed) ? normalizedRows : normalizedRows[0]);
    return new File([content], file.name, { type: file.type || "application/json" });
};

const normalizeCsvMfFile = async (file, nowDefaultNames) => {
    const text = await file.text();
    const lines = text.replace(/\r/g, "").split("\n");
    if (lines.length < 2) return file;

    const headers = parseCsvRow(lines[0]);
    const targetIndexes = headers
        .map((header, index) => (nowDefaultNames.has(header) ? index : -1))
        .filter((index) => index !== -1);

    if (!targetIndexes.length) return file;

    const normalizedLines = [lines[0]];
    for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) {
            normalizedLines.push(lines[i]);
            continue;
        }

        const row = parseCsvRow(lines[i]);
        targetIndexes.forEach((index) => {
            if (row[index] === undefined || row[index] === "") {
                row[index] = "null";
            }
        });
        normalizedLines.push(toCsvRow(row));
    }

    return new File([normalizedLines.join("\n")], file.name, { type: file.type || "text/csv" });
};

const parseCsvRow = (row) => {
    const result = [];
    let currentField = "";
    let insideQuotes = false;

    for (const char of row) {
        if (char === '"') {
            insideQuotes = !insideQuotes;
        } else if (char === "," && !insideQuotes) {
            result.push(currentField);
            currentField = "";
        } else {
            currentField += char;
        }
    }

    result.push(currentField);
    return result;
};

const toCsvRow = (fields = []) =>
    fields
        .map((value) => {
            const str = String(value ?? "");
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        })
        .join(",");